import { useEffect, useState } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import BoatTypeGraphic from "./BoatTypeGraphic";
import { mediaUrl } from "../lib/api";

export const BoatGallery = ({ images = [], displayName, assetTypeName }) => {
  const [index, setIndex] = useState(0);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
      if (e.key === "ArrowRight") setIndex((i) => (i + 1) % images.length);
      if (e.key === "ArrowLeft") setIndex((i) => (i - 1 + images.length) % images.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, images.length]);

  if (!images.length) {
    return (
      <div data-testid="boat-gallery-fallback" className="overflow-hidden rounded-3xl border border-sand-border">
        <BoatTypeGraphic typeName={assetTypeName} />
        <p className="bg-sand-card px-4 py-3 text-xs text-slate-600">
          No photographs are available for this boat yet.
        </p>
      </div>
    );
  }

  const current = images[index];
  const alt = current.title || current.caption || `${displayName} — ${assetTypeName || "boat"}`;

  return (
    <div data-testid="boat-gallery">
      <button
        type="button"
        data-testid="boat-gallery-open-lightbox"
        onClick={() => setOpen(true)}
        aria-label={`Open larger view of ${alt}`}
        className="block w-full overflow-hidden rounded-3xl border border-sand-border bg-sand-card"
      >
        <img
          src={mediaUrl(current.url)}
          alt={alt}
          width={1200}
          height={800}
          className="w-full object-cover transition-transform duration-500 hover:scale-[1.02]"
          style={{ aspectRatio: "3 / 2" }}
        />
      </button>

      {images.length > 1 && (
        <div className="mt-3 flex gap-3 overflow-x-auto pb-1">
          {images.map((img, i) => (
            <button
              key={img.assetPictureId ?? i}
              type="button"
              data-testid={`boat-gallery-thumb-${i}`}
              onClick={() => setIndex(i)}
              aria-label={`Show image ${i + 1} of ${images.length}`}
              aria-current={i === index}
              className={`h-20 w-28 shrink-0 overflow-hidden rounded-xl border-2 transition-colors ${
                i === index ? "border-ember" : "border-transparent hover:border-lake/50"
              }`}
            >
              <img
                src={mediaUrl(img.url)}
                alt={img.title || img.caption || `${displayName} image ${i + 1}`}
                loading="lazy"
                width={224}
                height={160}
                className="h-full w-full object-cover"
              />
            </button>
          ))}
        </div>
      )}

      {current.caption && <p className="mt-3 text-sm text-slate-600">{current.caption}</p>}

      {open && (
        <div
          data-testid="boat-gallery-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label={`${displayName} photo viewer`}
          className="fixed inset-0 z-[60] flex items-center justify-center bg-navy/95 p-4"
        >
          <button
            type="button"
            data-testid="boat-gallery-close-lightbox"
            onClick={() => setOpen(false)}
            aria-label="Close photo viewer"
            autoFocus
            className="absolute right-4 top-4 inline-flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20"
          >
            <X className="h-6 w-6" aria-hidden="true" />
          </button>
          {images.length > 1 && (
            <>
              <button
                type="button"
                onClick={() => setIndex((i) => (i - 1 + images.length) % images.length)}
                aria-label="Previous image"
                className="absolute left-4 inline-flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20"
              >
                <ChevronLeft className="h-6 w-6" aria-hidden="true" />
              </button>
              <button
                type="button"
                onClick={() => setIndex((i) => (i + 1) % images.length)}
                aria-label="Next image"
                className="absolute right-4 top-1/2 inline-flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20"
              >
                <ChevronRight className="h-6 w-6" aria-hidden="true" />
              </button>
            </>
          )}
          <img src={mediaUrl(current.url)} alt={alt} className="max-h-[85vh] max-w-full rounded-xl object-contain" />
        </div>
      )}
    </div>
  );
};

export default BoatGallery;
